const { merge } = require('lodash');

export function toNumber(value) {
  const numValue = parseInt(value, 10);
  return Number.isFinite(numValue) ? numValue : 0;
}

// assembles asn and site paths used for grouping
// and returns asn details for color schemes and legends
export function getGroupPathFromProbe(probe, { uniqueValues = true } = {}) {
  const { asNumbers, sites, asns } = (probe?.hops || []).reduce(
    (acc, hop) => {
      const { asn, site } = hop;

      if (asn) {
        acc.asNumbers.push(+asn.id);
        acc.asns[asn.id] = asn;
      }

      if (site) {
        acc.sites.push(+site.id);
      }

      return acc;
    },
    { asNumbers: [], sites: [], asns: {} }
  );

  let asPath = asNumbers;
  let sitePath = sites;

  if (uniqueValues) {
    asPath = [...new Set(asPath)];
    sitePath = [...new Set(sitePath)];
  }

  return { asPath, sitePath, asns };
}

// used as part of the backend to api transformation
// helps assemble reference data of nodes as well as asn path and ip/latency hops
function getProbeDetails(probe) {
  const probeGroupPaths = getGroupPathFromProbe(probe, { uniqueValues: false });

  return (probe?.hops || []).reduce(
    (acc, hop) => {
      const { ip, asn, geo, site, dns, latency, ingress_interface, egress_interface } = hop;

      if (ip && !acc.nodes[ip]) {
        acc.nodes[ip] = {
          ip,
          asn: asn?.id ? +asn.id : null,
          as_name: asn?.name,
          location: {
            country: geo?.country?.name,
            region: geo?.region?.name || '-',
            city: geo?.city?.name || '-',
            latitude: geo?.city?.latitude,
            longitude: geo?.city?.longitude
          },
          dns_name: dns?.hostname,
          site_id: site?.id,
          device_id: (ingress_interface || egress_interface || {}).device_id
        };
      }

      acc.hops.push({ node_id: ip || '', latency: +latency });

      acc.as_path = probeGroupPaths.asPath;

      return acc;
    },
    { hops: [], as_path: [], nodes: {} }
  );
}

function getProbes(probes) {
  return (probes || []).reduce(
    (acc, probe) => {
      const { completed: is_complete } = probe;
      const { hops, as_path, nodes } = getProbeDetails(probe);

      acc.traces.push({
        as_path,
        hops,
        is_complete
      });

      acc.max_as_path_length = Math.max(acc.max_as_path_length, as_path.length);

      acc.nodes = {
        ...acc.nodes,
        ...nodes
      };

      return acc;
    },
    { traces: [], max_as_path_length: 0, nodes: {} }
  );
}

export function backendToAPIResponse(response) {
  const { trace_time_series } = response || {};

  return Object.entries(trace_time_series || {}).reduce(
    (acc, [tsKey, tsData]) => {
      Object.values(tsData.tasks || {}).forEach((taskData) => {
        Object.entries(taskData.agents || {}).forEach(([agentId, agentData]) => {
          const { target, hop_count, probes } = agentData;
          const totalHopCount = hop_count?.total;
          const { traces, max_as_path_length, nodes } = getProbes(probes);
          const path = {
            agent_id: agentId,
            target_ip: target?.target_ip,
            hop_count: {
              average: totalHopCount,
              min: totalHopCount,
              max: totalHopCount
            },
            time: {
              seconds: tsKey,
              nanos: 0
            },
            traces,
            max_as_path_length
          };

          // add the new path and merge in discovered nodes
          acc.paths.push(path);
          acc.nodes = {
            ...acc.nodes,
            ...nodes
          };
        });
      });

      return acc;
    },
    { paths: [], nodes: {} }
  );
}

// used in preview tests, rolls up results into the last timestamp since results can sprinkle in across multiple timestamps
export function rollupTraceTimeSeries(response) {
  return Object.entries(response?.trace_time_series || {}).reduce((acc, [, tsData], i, allSeries) => {
    // roll up all the time series data into the last timestamp
    const [lastTsKey] = allSeries[allSeries.length - 1];
    acc.trace_time_series[lastTsKey] = merge(acc.trace_time_series[lastTsKey], tsData);

    return acc;
  }, response);
}
